import React, { useEffect, useRef, useState } from 'react';
import ClientInfo from './ClientInfo';
import ContactPanel from './ContactPanel';
import { fetchWithCorpId } from '../utils/api';
import { CONTACT_OWNERS, hasBillingEmail, listContacts, saveContacts } from '../utils/contacts';
import { useParams } from 'react-router-dom';
import { FormLoading } from './ui/formkit';
import StepFlow, { FlowNote, TabAttentionDot } from './ui/StepFlow';
import { useToast } from './ui/Toast';
import { toDateInputValue } from '../utils/dateUtils';

/**
 * Used in two contexts:
 *   1. Standalone page (route /on/clients/new or /on/clients/edit/:id)
 *      → id from useParams, renders its own Save/Cancel buttons.
 *   2. Inside a Drawer (from ClientTable)
 *      → id from `clientId` prop; pass `formRef` so the drawer footer
 *        can trigger submit; pass `hideActions` to skip the inline buttons.
 *
 * Create mode is a two-step flow (Details, Contacts) and always renders its
 * own step footer; edit mode keeps the tabs.
 */
const DEFAULT_CLIENT = {
  name: '', status: '', startDate: '', invoiced: false, accessCode: '', contacts: [],
};

const CREATE_STEPS = ['Details', 'Contacts'];

const BILLING_RULE = "Invoices are emailed to this client's Billing contacts. Without a Billing contact that has an email, invoices cannot be sent.";
const EMAIL_RE = /^\S+@\S+\.\S+$/;

// Mirrors the backend's invoice send guard (InvoiceEmailRecipients.hasTo():
// a contact whose role equals "Billing" ignoring case with a non-blank email).
function ClientForm({
  mode = 'create',
  clientId,                 // optional id, overrides URL param
  formRef,                  // optional ref attached to <form>
  hideActions = false,      // hide inline Save/Cancel (edit mode only)
  onSuccess,
  onCancel,
}) {
  const params = useParams();
  const toast = useToast();
  const id = clientId ?? params.id;
  const isEdit = mode === 'edit';
  const [activeTab, setActiveTab] = useState('info');
  const [step, setStep] = useState(1);
  const [client, setClient] = useState(DEFAULT_CLIENT);
  const [message, setMessage] = useState('');
  const [notice, setNotice] = useState('');
  const [loadingRecord, setLoadingRecord] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const confirmedNoBillingRef = useRef(false);
  const initialSnapshotRef = useRef(isEdit ? null : JSON.stringify(DEFAULT_CLIENT));
  // what the server currently holds, so saveContacts knows which rows were removed
  const loadedContactsRef = useRef([]);

  useEffect(() => {
    const fetchClient = async () => {
      if (isEdit && id) {
        setLoadingRecord(true);
        try {
          const data = await fetchWithCorpId(`/clients/${id}`);
          // contacts come from their own endpoint since backend #148
          const contacts = await listContacts(CONTACT_OWNERS.CLIENT, id);
          const normalized = {
            ...data,
            startDate: toDateInputValue(data.startDate),
            contacts,
          };
          loadedContactsRef.current = contacts;
          setClient(normalized);
          initialSnapshotRef.current = JSON.stringify(normalized);
        } catch (error) {
          setMessage('Could not load this client: ' + error.message);
        } finally {
          setLoadingRecord(false);
        }
      } else {
        setLoadingRecord(false);
      }
    };
    fetchClient();
  }, [isEdit, id]);

  const isDirty = initialSnapshotRef.current !== null
    && JSON.stringify(client) !== initialSnapshotRef.current;

  const billingEmailPresent = hasBillingEmail(client.contacts);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    let nextValue = type === 'checkbox' ? Boolean(checked) : value;
    if (name === 'invoiced' && type !== 'checkbox') {
      nextValue = value === 'true';
    }
    setClient((prev) => ({ ...prev, [name]: nextValue }));
  };

  const goToStep = (n) => {
    setMessage('');
    setNotice('');
    confirmedNoBillingRef.current = false;
    setStep(n);
  };

  const advanceStep = () => {
    if (step === 1 && !(client.name || '').trim()) {
      setMessage('Fill in the required fields: Client name.');
      return;
    }
    goToStep(Math.min(step + 1, CREATE_STEPS.length));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && step < CREATE_STEPS.length) {
      advanceStep();
      return;
    }
    if (saving) return;
    setMessage('');

    if (!isEdit) {
      const badEmail = (client.contacts || []).some(
        (c) => c.email1 && c.email1.trim() && !EMAIL_RE.test(c.email1.trim())
      );
      if (badEmail) {
        setNotice('');
        setMessage('Enter a valid email address for each contact.');
        return;
      }
      if (!billingEmailPresent && !confirmedNoBillingRef.current) {
        confirmedNoBillingRef.current = true;
        setNotice('No Billing contact with an email yet. Invoices cannot be sent to this client until one is added. Click Create client again to continue.');
        return;
      }
      setNotice('');
    }

    setSaving(true);
    try {
      const url = isEdit && client.id ? `/clients/${client.id}` : '/clients';
      // Contacts are no longer part of the client payload (backend #148): they
      // are saved against the client's own /contacts endpoints once it exists,
      // which is also the only way a brand new client can have any.
      const { contacts, ...clientPayload } = client;
      const response = await fetchWithCorpId(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientPayload),
      });
      if (response) {
        const savedId = response.id || client.id;
        const saved = await saveContacts(
          CONTACT_OWNERS.CLIENT, savedId, contacts, loadedContactsRef.current);
        loadedContactsRef.current = saved;
        initialSnapshotRef.current = JSON.stringify({ ...client, contacts: saved });
        toast.success(isEdit ? 'Client updated' : 'Client created');
        onSuccess?.();
      } else {
        setMessage('Failed to save. Please try again.');
      }
    } catch (error) {
      setMessage(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabStyle = (tab) => ({
    padding: '10px 18px',
    cursor: 'pointer',
    background: activeTab === tab ? 'var(--surface-card)' : 'transparent',
    color: activeTab === tab ? 'var(--ink)' : 'var(--muted)',
    border: 'none',
    borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
    fontFamily: 'var(--font-sans)',
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: 0,
    transition: 'background 0.16s, color 0.16s',
  });

  if (loadingRecord) {
    return <FormLoading label="Loading client…" />;
  }

  const contactsEditor = (
    <ContactPanel
      showRoles
      contacts={client.contacts || []}
      setContacts={(updatedContacts) => setClient(prev => ({ ...prev, contacts: updatedContacts }))}
    />
  );

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      noValidate={!isEdit}
      data-dirty={isDirty ? 'true' : 'false'}
    >
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}
      {notice && (
        <div className="alert warning" role="alert" style={{ marginBottom: '20px' }}>
          {notice}
        </div>
      )}

      {isEdit ? (
        <>
          <div role="tablist" style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--hairline)', marginBottom: '24px' }}>
            <button type="button" role="tab" aria-selected={activeTab === 'info'} style={tabStyle('info')} onClick={() => setActiveTab('info')}>Info</button>
            <button type="button" role="tab" aria-selected={activeTab === 'contact'} style={tabStyle('contact')} onClick={() => setActiveTab('contact')}>
              Contacts
              {!billingEmailPresent && <TabAttentionDot label="No Billing contact with an email" />}
            </button>
          </div>

          {activeTab === 'info' && (
            <ClientInfo client={client} handleChange={handleChange} />
          )}
          {activeTab === 'contact' && (
            <>
              <FlowNote>{BILLING_RULE}</FlowNote>
              {contactsEditor}
            </>
          )}

          {!hideActions && (
            <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              {onCancel && (
                <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <StepFlow steps={CREATE_STEPS} current={step} onStepClick={goToStep} />

          {step === 1 && (
            <ClientInfo client={client} handleChange={handleChange} />
          )}
          {step === 2 && (
            <>
              <FlowNote>{BILLING_RULE}</FlowNote>
              {contactsEditor}
            </>
          )}

          <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
            {step === 1 && !hideActions && onCancel && (
              <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
            )}
            {step > 1 && (
              <button type="button" className="btn-secondary" onClick={() => goToStep(step - 1)} disabled={saving}>
                Back
              </button>
            )}
            <button type="submit" className="btn-primary" disabled={saving}>
              {step === 1 ? 'Next: Contacts' : (saving ? 'Saving…' : 'Create client')}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

export default ClientForm;
